import React from 'react';
import maxBy from 'lodash/maxBy';
import { read, utils } from 'xlsx';
import { Box, Button, Checkbox, Grid, styled, Typography } from '@mui/material';
import { DataGridPremium, type GridColDef } from '@mui/x-data-grid-premium';

import { type FilesViewerProps } from '../FilesViewer';
import { renderCellWithTooltip } from '../../page-layouts/view/table/ViewTableUtils';

interface XlsxTableFooterProps {
	xlsxNames: string[];
	currentSheetIndex: number;
	setCurrentSheetIndex: React.Dispatch<React.SetStateAction<number>>;
}

declare module '@mui/x-data-grid-premium' {
	interface FooterPropsOverrides extends XlsxTableFooterProps {}
}

const StyledDataGrid = styled(DataGridPremium)(({ theme }) => ({
	'.MuiDataGrid-cell': {
		display: 'flex',
		alignItems: 'center',
		fontVariantNumeric: 'tabular-nums'
	},
	'.MuiDataGrid-columnHeader.XlsxViewer-headerCheckbox': {
		paddingLeft: 6,
		paddingRight: 6
	}
}));

function XlsxTableFooter({ xlsxNames, currentSheetIndex, setCurrentSheetIndex }: XlsxTableFooterProps) {
	return (
		<Grid container justifyContent="space-between" alignItems="center" spacing={1} className="p-8">
			<Grid size="grow" container alignItems="center" spacing={1}>
				{xlsxNames.map((xlsxName, index) => (
					<Grid key={index}>
						<Button
							size="small"
							color={index === currentSheetIndex ? 'secondary' : 'primary'}
							onClick={() => setCurrentSheetIndex(index)}
						>
							{xlsxName}
						</Button>
					</Grid>
				))}
			</Grid>
		</Grid>
	);
}

/** This component can render a spreadsheet file (Excel's XLSX) */
export default function XlsxViewer({ fileEncoded }: FilesViewerProps) {
	const [currentSheetIndex, setCurrentSheetIndex] = React.useState(0);
	const [xlsxSheets, setXlsxSheets] = React.useState<any[][]>([]);
	const [xlsxNames, setXlsxNames] = React.useState<string[]>([]);

	const [showHeaderRow, setShowHeaderRow] = React.useState(true);

	React.useEffect(() => {
		const workbook = read(fileEncoded, { type: 'base64' });
		const names = Object.keys(workbook.Sheets);
		const sheets = names.map(name => utils.sheet_to_json(workbook.Sheets[name], { header: 1 }));

		setXlsxSheets(sheets);
		setXlsxNames(names);
	}, [fileEncoded]);

	const rows: any[] = [];
	const columns: GridColDef[] = [
		{
			field: '0',
			headerName: '',
			width: 50,
			headerClassName: 'XlsxViewer-headerCheckbox',
			renderHeader: () => (
				<Checkbox size="small" checked={showHeaderRow} onChange={e => setShowHeaderRow(e.target.checked)} />
			),
			renderCell: ({ value }) => (
				<Typography className="w-full text-center" variant="subtitle2">
					{value}
				</Typography>
			),
			sortable: false
		}
	];

	const headerRow: any[] = xlsxSheets[currentSheetIndex]?.[0];

	xlsxSheets[currentSheetIndex]
		?.filter((_, index) => (showHeaderRow && index !== 0) || !showHeaderRow)
		.forEach((array, rowIndex) => {
			const fields = Object.values(array);

			if (rowIndex === 0) {
				const rowLength = maxBy(xlsxSheets[currentSheetIndex], (row: any[]) => row.length)?.length;

				for (let index = 0; index < (rowLength || 0); index += 1)
					columns.push({
						field: `${index + 1}`,
						headerName: String.fromCharCode('A'.charCodeAt(0) + index),
						minWidth: 150,
						flex: 1,
						renderHeader: ({ colDef }) => (
							<Typography variant="subtitle2">
								{showHeaderRow ? headerRow[index] : colDef.headerName}
							</Typography>
						),
						renderCell: renderCellWithTooltip
					});
			}

			const row = { id: rowIndex, 0: rowIndex + 1 };
			fields.forEach((cell, index) => {
				row[index + 1] = cell;
			});
			rows.push(row);
		});

	return xlsxSheets[currentSheetIndex] ? (
		<Box className="h-full w-full">
			<StyledDataGrid
				columns={columns}
				rows={rows}
				slots={{
					footer: XlsxTableFooter
				}}
				slotProps={{
					footer: { xlsxNames, currentSheetIndex, setCurrentSheetIndex }
				}}
				density="compact"
				disableColumnMenu
				disableRowSelectionOnClick
				showColumnVerticalBorder
				showCellVerticalBorder
			/>
		</Box>
	) : (
		<></>
	);
}
